import { useEffect, useRef, useState } from 'react'

// Apple-accepted parental gate for Kids Category. Random simple addition
// (operands 2-10, sum always under 20) — adults solve in ~2s, pre-readers
// can't pass without help. Wrong answer shakes the card and rerolls so the
// kid can't brute-force by trying common numbers.
function generate() {
  const a = 2 + Math.floor(Math.random() * 9)
  const b = 2 + Math.floor(Math.random() * 9)
  return { a, b, sum: a + b }
}

export function ParentalGate({ open, title = 'Quick check', subtitle = 'Solve the math problem to continue.', onSuccess, onCancel }) {
  const [problem, setProblem] = useState(generate)
  const [answer, setAnswer] = useState('')
  const [shake, setShake] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!open) return
    setProblem(generate())
    setAnswer('')
    setShake(false)
    const t = setTimeout(() => inputRef.current?.focus(), 350)
    return () => clearTimeout(t)
  }, [open])

  if (!open) return null

  const submit = () => {
    if (parseInt(answer, 10) === problem.sum) {
      onSuccess?.()
    } else {
      setShake(true)
      setTimeout(() => setShake(false), 460)
      setProblem(generate())
      setAnswer('')
      inputRef.current?.focus()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-5"
      style={{ background: 'rgba(0,0,0,0.6)', animation: 'crossfade-in 200ms ease-out both' }}
      role="dialog"
      aria-modal="true"
      aria-label="Parental gate"
    >
      <div
        className="w-full max-w-sm rounded-3xl bg-white px-6 pt-7 pb-6 shadow-2xl"
        style={{
          animation: shake ? 'shake 0.45s' : 'page-modal 0.4s cubic-bezier(0.16, 1, 0.3, 1) both',
        }}
      >
        <p className="text-center text-[12px] uppercase tracking-[0.18em] text-stone-500 font-semibold mb-1">
          For a grown-up
        </p>
        <h2 className="text-center text-[22px] font-bold text-stone-800 mb-1">
          {title}
        </h2>
        <p className="text-center text-[14px] text-stone-500 mb-6">
          {subtitle}
        </p>

        <div className="text-center mb-5">
          <p className="text-[44px] font-bold text-stone-800 tabular-nums leading-none">
            {problem.a} + {problem.b} = ?
          </p>
        </div>

        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          value={answer}
          onChange={(e) => setAnswer(e.target.value.replace(/\D/g, '').slice(0, 3))}
          onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
          placeholder="Your answer"
          className="w-full px-4 py-3 mb-4 text-center text-[20px] font-semibold tabular-nums rounded-xl border-2 border-stone-200 focus:outline-none focus:border-stone-400 placeholder:text-stone-300"
          aria-label="Your answer"
        />

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn-neutral flex-1 py-3 text-[15px]"
            style={{
              borderTop: '2px solid #EFE9E0',
              borderLeft: '2px solid #EFE9E0',
              borderRight: '2px solid #EFE9E0',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!answer}
            className="btn-green flex-1 py-3 text-[15px]"
            style={{
              borderTop: '2px solid var(--color-mp-green-700)',
              borderLeft: '2px solid var(--color-mp-green-700)',
              borderRight: '2px solid var(--color-mp-green-700)',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  )
}
